import React, { useMemo } from "react";
import { useDuel } from "@/providers/duel-provider";
import { useAuth } from "@/providers/auth-provider";
import { useUser } from "@/providers/user-provider";
import { DuelDraftScene } from "@/lib/duel/duel-draft-scene";
import { DuelScreenMiddleSection } from "../duel-queue/duel-screen-middle-section";
import { Pokemon } from "prisma-layer";
import { DuelPokemon } from "types/duel-dynamodb";

interface DuelDraftPixiOverlayProps {
  isVisible: boolean;
  draftScene: DuelDraftScene | null;
}

export const DuelDraftPixiOverlay = ({
  isVisible,
  draftScene,
}: DuelDraftPixiOverlayProps) => {
  const { duelState, submitPick, submitBan } = useDuel();
  const { playerCountry } = useUser();

  // Local state for tracking selection (before confirm)
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const [inspectedPokemon, setInspectedPokemon] = React.useState<
    (DuelPokemon & { team: "A" | "B" | undefined }) | null
  >(null);

  // Sync selection with scene when clicking
  React.useEffect(() => {
    if (!draftScene) return;

    draftScene.setOnPokemonClick((pokemonId) => {
      const stepType = duelState.current_step?.step_type;
      const pokemonTeam = draftScene.getPokemonTeam(pokemonId);

      const isPlayerA = duelState.player_role === "player_a";
      const myTeamSide = isPlayerA ? "A" : "B";
      const opponentTeamSide = isPlayerA ? "B" : "A";

      // During ban phase, can only select opponent's pokemon
      // During pick phase, can only select your own pokemon
      const isInvalidTarget =
        (stepType === "ban" && pokemonTeam === myTeamSide) ||
        (stepType === "pick" && pokemonTeam === opponentTeamSide);

      if (isInvalidTarget) {
        // Can't select this pokemon - clear selection and just show info
        setSelectedIds([]);
        const pokemonData = draftScene.getPokemon(pokemonId);

        if (pokemonData) {
          setInspectedPokemon({ ...pokemonData, team: pokemonTeam });
        }
        return;
      }

      setSelectedIds((prev) => {
        const isSelected = prev.includes(pokemonId);
        const maxPicks = duelState.current_step?.picks_count || 1;

        let newSelection: number[] = [];
        if (isSelected) {
          newSelection = prev.filter((id) => id !== pokemonId);
        } else {
          if (prev.length >= maxPicks && maxPicks === 1) {
            newSelection = [pokemonId];
          } else if (prev.length < maxPicks) {
            newSelection = [...prev, pokemonId];
          } else {
            newSelection = prev;
          }
        }

        // Update inspected pokemon
        const pokemonData = draftScene.getPokemon(pokemonId);
        if (pokemonData) {
          setInspectedPokemon({ ...pokemonData, team: pokemonTeam });
        }

        return newSelection;
      });
    });
  }, [
    draftScene,
    duelState.current_step?.picks_count,
    duelState.current_step?.step_type,
    duelState.player_role,
  ]);

  // Update Scene Visuals
  React.useEffect(() => {
    if (!draftScene) return;

    const isPlayerA = duelState.player_role === "player_a";

    const myPicked = duelState.your_picks || [];
    const oppPicked = duelState.opponent_picks || [];
    const myBanned = duelState.your_banned_picks || [];
    const oppBanned = duelState.opponent_banned_picks || [];
    const allBanned = [...myBanned, ...oppBanned];

    // Team A picks should show as blue, Team B picks as red
    // For player_a: your_picks = Team A (blue), opponent_picks = Team B (red)
    // For player_b: your_picks = Team B (red), opponent_picks = Team A (blue)
    const teamAPicked = isPlayerA ? myPicked : oppPicked;
    const teamBPicked = isPlayerA ? oppPicked : myPicked;

    draftScene.updateVisuals(selectedIds, allBanned, teamAPicked, teamBPicked);
  }, [
    draftScene,
    selectedIds,
    duelState.your_picks,
    duelState.opponent_picks,
    duelState.your_banned_picks,
    duelState.opponent_banned_picks,
    duelState.player_role,
  ]);

  // Track which step index we submitted for, to clear selection when step changes
  const submittedStepRef = React.useRef<number | null>(null);

  // Clear selection when step changes (server responded)
  React.useEffect(() => {
    const currentStepIndex = duelState.current_step?.step_index ?? null;
    if (
      submittedStepRef.current !== null &&
      currentStepIndex !== submittedStepRef.current
    ) {
      // Step changed, clear the selection
      setSelectedIds([]);
      submittedStepRef.current = null;
    }
  }, [duelState.current_step?.step_index]);

  // Handle Confirm
  const handleConfirm = () => {
    if (!duelState.current_step) return;
    const { picks_count, step_type, step_index } = duelState.current_step;

    if (selectedIds.length !== picks_count) return;

    // Track which step we submitted for
    submittedStepRef.current = step_index;

    if (step_type === "pick") {
      submitPick(selectedIds);
    } else {
      submitBan(selectedIds);
    }
    // Don't clear selectedIds here - keep visual until server responds
    setInspectedPokemon(null);
  };

  const handleCancelInspect = () => {
    setInspectedPokemon(null);
  };

  const teamNames = useMemo(() => {
    const isPlayerA = duelState.player_role === "player_a";
    return {
      blueTeamName: isPlayerA
        ? duelState.your_team_name || "You"
        : duelState.opponent_team_name || "Opponent",
      redTeamName: isPlayerA
        ? duelState.opponent_team_name || "Opponent"
        : duelState.your_team_name || "You",
      blueTeamCountry: isPlayerA
        ? playerCountry ?? null
        : duelState.opponent_player?.country || null,
      redTeamCountry: isPlayerA
        ? duelState.opponent_player?.country || null
        : playerCountry ?? null,
    };
  }, [
    duelState.your_team_name,
    duelState.opponent_team_name,
    duelState.opponent_player,
    playerCountry,
    duelState.player_role,
  ]);

  // Calculate the vertical center of the game canvas (map is 480x960, centered in container)
  // The middle section should be at 50% of the map height, accounting for canvas scaling/centering
  const [canvasBounds, setCanvasBounds] = React.useState<{ top: number; height: number } | null>(null);

  React.useEffect(() => {
    const updateCanvasBounds = () => {
      const container = document.querySelector('[data-duel-canvas]') as HTMLElement | null;
      if (!container) return;

      const mapWidth = 480;
      const mapHeight = 960;
      const scaleX = container.clientWidth / mapWidth;
      const scaleY = container.clientHeight / mapHeight;
      const scale = Math.min(scaleX, scaleY, 1);

      const scaledHeight = mapHeight * scale;
      const topOffset = (container.clientHeight - scaledHeight) / 2;

      setCanvasBounds({ top: topOffset, height: scaledHeight });
    };

    // Small delay to ensure canvas is mounted
    const timer = setTimeout(updateCanvasBounds, 50);
    window.addEventListener('resize', updateCanvasBounds);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateCanvasBounds);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  // Fallback to CSS centering if bounds not calculated yet
  const useCalculatedPosition = canvasBounds && canvasBounds.height > 0;
  const middleSectionTop = useCalculatedPosition 
    ? canvasBounds.top + canvasBounds.height * 0.5 
    : undefined;

  return (
    <div className="absolute inset-0 pointer-events-none flex flex-col justify-between">
      {/* Middle Section (Info & Actions) */}
      <div
        className="absolute left-0 right-0 flex justify-center pointer-events-none z-40 overflow-visible"
        style={useCalculatedPosition 
          ? { top: middleSectionTop, transform: 'translateY(-50%)' }
          : { top: '50%', transform: 'translateY(-50%)' }
        }
      >
        <div className="pointer-events-auto w-full flex justify-center">
          <DuelScreenMiddleSection
            handleSubmitSelection={handleConfirm}
            selectedPokemonIds={selectedIds}
            picksCount={duelState.current_step?.picks_count || 0}
            duelState={duelState}
            teamNames={teamNames}
            inspectedPokemon={inspectedPokemon}
            onCancelInspect={handleCancelInspect}
            stepType={duelState.current_step?.step_type}
            introStatus="completed"
          />
        </div>
      </div>
    </div>
  );
};
